/**
 * OrionFocusLighting
 * A three-point lighting rig (key / fill / rim) that illuminates the focused
 * Orion module while drilled into a single deployed module/attachment. Mounted
 * only when focused; the lights intentionally do NOT cast shadows.
 *
 * Rendered at the SCENE ROOT (world space). Point lights did not visibly affect
 * the model under the WebGPU/TSL renderer, so this uses spot lights (the
 * directional `Lighting` rig is the proven pattern here).
 *
 * Each frame the rig group is positioned at the focused module's spin-pivot world
 * position — the SAME anchor the dev SectionPivotGizmo (magenta ball) uses:
 * the section group's live world transform applied to (spinPivot+offset). This is
 * the true module centre (NOT the camera-offset look-at point), so the rig and
 * its converging spot targets re-centre on whichever module is focused.
 *
 * Light positions in `config` are OFFSETS from that centre; each spot aims back
 * at the centre (target at the rig-group origin).
 *
 * The lights stay MOUNTED whenever the rig is rendered and only their intensity
 * is gated on `active` — mounting/unmounting spot lights forces the WebGPU
 * renderer to recompile material pipelines (a visible frame stutter the first
 * time they appear), so we keep them resident (intensity 0 when inactive) so the
 * compile happens once, up front, rather than on section focus.
 *
 * Light positions/cone shape are shared; the three intensities are PER-SECTION
 * (OCM = the `integrity` section, ESM, ICPS) — each module gets its own look.
 *
 * Props:
 *   active         — when false the lights drop to intensity 0 (stay mounted)
 *   section        — focused section id ('integrity' | 'esm' | 'icps'); selects
 *                    which per-section intensity trio to use
 *   sectionGroupRef— ref to the focused section's group (live world transform)
 *   sectionPivot   — [x,y,z] local spin pivot (+offset) of that section
 *   config         — shared geometry (keyPos/Angle/Penumbra, …) + per-section
 *                    intensities (ocmKey/ocmFill/ocmRim, esm*, icps*)
 *   helper         — dev only; renders small marker spheres at each light + centre
 */
import { useRef, useEffect } from 'react'
import { useFrame } from '@react-three/fiber'
import { Object3D, Vector3 } from 'three'

const DEFAULTS = {
  keyPos: [0.25, 0.3, 0.35], keyAngle: 0.9, keyPenumbra: 0.6,
  fillPos: [-0.35, 0.12, 0.25], fillAngle: 1.0, fillPenumbra: 0.7,
  rimPos: [-0.12, 0.25, -0.4], rimAngle: 0.8, rimPenumbra: 0.5,
  // Per-section intensities (OCM = integrity)
  ocmKey: 1, ocmFill: 1, ocmRim: 1,
  esmKey: 1, esmFill: 0, esmRim: 1,
  icpsKey: 0, icpsFill: 0, icpsRim: 1,
}

// Maps the focused section id to its [key, fill, rim] intensity config keys.
const SECTION_INT_KEYS = {
  integrity: ['ocmKey', 'ocmFill', 'ocmRim'],
  esm: ['esmKey', 'esmFill', 'esmRim'],
  icps: ['icpsKey', 'icpsFill', 'icpsRim'],
}

const _pivotVec = new Vector3()

export function OrionFocusLighting({ active = false, section = null, sectionGroupRef = null, sectionPivot = null, config = {}, helper = false }) {
  const c = { ...DEFAULTS, ...config }
  const groupRef = useRef()
  const keyRef = useRef()
  const fillRef = useRef()
  const rimRef = useRef()
  const targetObj = useRef(new Object3D())

  // Aim all three spots at the rig-group origin (= the module centre).
  useEffect(() => {
    const t = targetObj.current
    ;[keyRef, fillRef, rimRef].forEach((r) => { if (r.current) r.current.target = t })
  })

  useFrame(() => {
    if (!groupRef.current) return
    // Keep the rig parked on the focused module's centre while active; when
    // inactive the lights are at intensity 0 so position doesn't matter.
    if (active) {
      const sg = sectionGroupRef?.current
      const piv = sectionPivot ?? [0, 0, 0]
      if (sg) {
        sg.updateWorldMatrix(true, false)
        _pivotVec.set(piv[0], piv[1], piv[2]).applyMatrix4(sg.matrixWorld)
        groupRef.current.position.copy(_pivotVec)
      }
    }
    targetObj.current.updateMatrixWorld()
  })

  // Per-section intensities, gated on `active` so the lights stay mounted
  // (compiled) but dark when not focused — avoids the on-mount shader-recompile
  // stutter. Unknown section → 0 (no contribution).
  const [kKey, kFill, kRim] = SECTION_INT_KEYS[section] ?? []
  const keyInt = active && kKey ? c[kKey] : 0
  const fillInt = active && kFill ? c[kFill] : 0
  const rimInt = active && kRim ? c[kRim] : 0

  return (
    <group ref={groupRef}>
      {/* Shared aim point at the module centre (rig-group origin) */}
      <primitive object={targetObj.current} />

      {/* Key */}
      <spotLight ref={keyRef} position={c.keyPos} intensity={keyInt} angle={c.keyAngle} penumbra={c.keyPenumbra} distance={0} decay={0} color="#ffffff" />
      {/* Fill */}
      <spotLight ref={fillRef} position={c.fillPos} intensity={fillInt} angle={c.fillAngle} penumbra={c.fillPenumbra} distance={0} decay={0} color="#cfe0ff" />
      {/* Rim / back */}
      <spotLight ref={rimRef} position={c.rimPos} intensity={rimInt} angle={c.rimAngle} penumbra={c.rimPenumbra} distance={0} decay={0} color="#ffe9d6" />

      {helper && active && (
        <>
          {/* centre (white), key (green), fill (cyan), rim (orange) */}
          <mesh>
            <sphereGeometry args={[0.015, 12, 12]} />
            <meshBasicMaterial color="#ffffff" depthTest={false} transparent opacity={0.95} />
          </mesh>
          <mesh position={c.keyPos}>
            <sphereGeometry args={[0.015, 12, 12]} />
            <meshBasicMaterial color="#00ff00" depthTest={false} transparent opacity={0.95} />
          </mesh>
          <mesh position={c.fillPos}>
            <sphereGeometry args={[0.015, 12, 12]} />
            <meshBasicMaterial color="#00ffff" depthTest={false} transparent opacity={0.95} />
          </mesh>
          <mesh position={c.rimPos}>
            <sphereGeometry args={[0.015, 12, 12]} />
            <meshBasicMaterial color="#ff8800" depthTest={false} transparent opacity={0.95} />
          </mesh>
        </>
      )}
    </group>
  )
}

export default OrionFocusLighting
